import { Box } from '@mui/material'
import AdaptiveTabs from 'components/AdaptiveTabs/AdaptiveTabs'
import { StyledTab } from 'components/StyledTab/StyledTab'
import TabPanel from 'components/TabPanel/TabPanel'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { selectMissionEditingZone } from 'store/selectors/mission'
import {
  DEFAULT_FRONT_DUST_SUPPRESSION,
  DEFAULT_TURBINE_SPEED,
  MISSION_PAGE_MODES,
  MISSION_ZONE_TYPES,
  SHOW_ZONE_SETTINGS_TAB,
} from 'utils/constants'
import { ICleaningZone, IZone, RootState } from 'utils/interfaces'

import ZoneCleaningSettings from './ZoneDetails/ZoneCleaningSettings'
import ZoneCoverageSettings from './ZoneDetails/ZoneCoverageSettings'
import ZoneDetail from './ZoneDetails/ZoneDetails'
import { OnSaveClickedParams, ZoneFormCache, getLatestZone } from './types'

const TAB_PANEL_BOX_SX = {
  p: 3,
  width: '100%',
  boxSizing: 'border-box',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
} as const

interface DrawZoneModeProps {
  onSaveClicked: (
    zoneName: string,
    zoneDescription: string,
    zoneType: MISSION_ZONE_TYPES,
    cleaningSpeed?: number,
    transportSpeed?: number,
    isBorderCleaning?: boolean,
    equipment_setup?: { turbine_speed?: number; front_dust_suppression?: boolean; scrubber_brush_speed?: number },
    setup?: {
      swath_sequence_mode?: number
      borders_num?: number
      main_thresholds?: { max_edge_angle?: number; max_edge_angle_for_borders?: number }
    },
    keepEditing?: boolean
  ) => Promise<void>
  zone?: IZone
  coverageOnly?: boolean
  setCalculationMode?: (mode: 'all' | 'single' | undefined) => void
}

const DrawZoneMode = ({ onSaveClicked, zone, coverageOnly, setCalculationMode }: DrawZoneModeProps) => {
  const adaptedOnSaveClicked = (params: OnSaveClickedParams) => {
    return onSaveClicked(
      params.zoneName,
      params.zoneDescription,
      params.zoneType,
      params.cleaningSpeed,
      params.transportSpeed,
      params.isBorderCleaning,
      params.equipment_setup,
      params.setup,
      params.keepEditing
    )
  }
  const { t } = useTranslation()
  const [tab, setTab] = useState(0)
  const editingZone = useSelector(selectMissionEditingZone)
  const missionMode = useSelector((state: RootState) => state.mission.mode)
  const drawingZoneType = useSelector((state: RootState) => state.mission.drawingZoneType)
  const isRouteMode =
    missionMode === MISSION_PAGE_MODES.EDIT_ZONE_ROUTE || missionMode === MISSION_PAGE_MODES.DRAW_ROUTE

  useEffect(() => {
    if (isRouteMode && tab === 2) {
      setTab(0)
    }
  }, [isRouteMode, tab])

  const zoneToCheck = editingZone || zone

  // Determine the zone type from existing zone if available, otherwise fall back to drawingZoneType from the store
  const zoneType = zoneToCheck?.properties?.zone_type ?? drawingZoneType
  // Show Settings tab if feature toggle is enabled AND zone is not restricted
  const isRestrictedZone = zoneType === MISSION_ZONE_TYPES.RESTRICT
  const showSettingsTab = SHOW_ZONE_SETTINGS_TAB && !isRestrictedZone

  // Determine the first tab label based on the mode
  const firstTabLabel = isRouteMode
    ? t('mission.route_editing')
    : zoneType === MISSION_ZONE_TYPES.CLEANING
    ? t('mission.cleaning_zone_editing')
    : t('mission.restrict_zone_editing')

  const cleaningZones = useSelector((state: RootState) => state.mission.cleaningZones)
  const latestZone = getLatestZone(cleaningZones, zoneToCheck?.properties?.zone_id)
  const finalZone = latestZone || zoneToCheck

  const formCacheRef = useRef<ZoneFormCache>({})
  const zoneIdRef = useRef<number | undefined>(zoneToCheck?.properties?.zone_id)

  useEffect(() => {
    const currentZoneId = zoneToCheck?.properties?.zone_id
    if (zoneIdRef.current !== currentZoneId) {
      zoneIdRef.current = currentZoneId
      formCacheRef.current = {}

      if (finalZone) {
        const zone = finalZone as ICleaningZone
        const props = zone.properties
        if (props) {
          if (props.name) formCacheRef.current.name = props.name
          if (props.description) formCacheRef.current.description = props.description
          if (props.zone_type) formCacheRef.current.selectedZoneType = props.zone_type as MISSION_ZONE_TYPES
          if (props.cleaning_speed !== undefined) formCacheRef.current.cleaningSpeed = props.cleaning_speed
          if (props.transport_speed !== undefined) formCacheRef.current.transportSpeed = props.transport_speed
          if (props.is_border_cleaning !== undefined) formCacheRef.current.isBorderCleaning = props.is_border_cleaning
          if (props.equipment_setup) {
            const eq = props.equipment_setup
            if (eq.turbine_speed !== undefined) formCacheRef.current.turbineSpeed = eq.turbine_speed
            if (eq.front_dust_suppression !== undefined)
              formCacheRef.current.frontDustSuppression = eq.front_dust_suppression
            if (eq.scrubber_brush_speed !== undefined) formCacheRef.current.scrubberBrushSpeed = eq.scrubber_brush_speed
          }
          if (props.setup) {
            const s = props.setup
            if (s.swath_sequence_mode !== undefined) formCacheRef.current.swathSequenceMode = s.swath_sequence_mode
            if (s.borders_num !== undefined) formCacheRef.current.bordersNum = s.borders_num
            if (s.main_thresholds) {
              if (s.main_thresholds.max_edge_angle !== undefined)
                formCacheRef.current.maxEdgeAngle = s.main_thresholds.max_edge_angle
              if (s.main_thresholds.max_edge_angle_for_borders !== undefined)
                formCacheRef.current.maxEdgeAngleForBorders = s.main_thresholds.max_edge_angle_for_borders
            }
          }
        }
      }
    }
  }, [finalZone, zoneToCheck, cleaningZones])

  const getCacheValue = <K extends keyof ZoneFormCache>(key: K): ZoneFormCache[K] => {
    return formCacheRef.current[key]
  }

  const setCacheValue = <K extends keyof ZoneFormCache>(key: K, value: ZoneFormCache[K]) => {
    formCacheRef.current[key] = value
  }

  const getAllCacheValues = (): ZoneFormCache => {
    return { ...formCacheRef.current }
  }

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setTab(newValue)
  }

  return (
    <div className="flex-column">
      {showSettingsTab && !coverageOnly && (
        <AdaptiveTabs
          value={tab}
          onChange={handleChange}
          indicatorColor="primary"
          sx={{ height: 45, minHeight: 45 }}
        >
          <StyledTab
            label={firstTabLabel}
            sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}
            disableRipple
          />
          <StyledTab
            label={t('mission.cleaning')}
            sx={{ fontSize: 16, fontWeight: '400', textTransform: 'none', letterSpacing: 0 }}
            disableRipple
          />
        </AdaptiveTabs>
      )}
      {coverageOnly ? (
        <Box sx={TAB_PANEL_BOX_SX}>
          <ZoneCoverageSettings
            onSaveClicked={adaptedOnSaveClicked}
            zone={zone}
            getCacheValue={getCacheValue}
            setCacheValue={setCacheValue}
            getAllCacheValues={getAllCacheValues}
            setCalculationMode={setCalculationMode}
          />
        </Box>
      ) : showSettingsTab ? (
        <>
          <TabPanel value={tab} index={0} disablePadding={true}>
            <Box sx={TAB_PANEL_BOX_SX}>
              <ZoneDetail
                onSaveClicked={adaptedOnSaveClicked}
                zone={zone}
                getCacheValue={getCacheValue}
                setCacheValue={setCacheValue}
                getAllCacheValues={getAllCacheValues}
              />
            </Box>
          </TabPanel>
          <TabPanel value={tab} index={1} disablePadding={true}>
            <Box sx={TAB_PANEL_BOX_SX}>
              <ZoneCleaningSettings
                onSaveClicked={adaptedOnSaveClicked}
                zone={zone}
                getCacheValue={getCacheValue}
                setCacheValue={setCacheValue}
                getAllCacheValues={getAllCacheValues}
              />
            </Box>
          </TabPanel>
        </>
      ) : (
        <Box sx={TAB_PANEL_BOX_SX}>
          <ZoneDetail
            onSaveClicked={adaptedOnSaveClicked}
            zone={zone}
            getCacheValue={getCacheValue}
            setCacheValue={setCacheValue}
            getAllCacheValues={getAllCacheValues}
          />
        </Box>
      )}
    </div>
  )
}

export default DrawZoneMode
